import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

import type { ExactMach1InstallationFiles } from "./browser-mach1-store";

const SHA256 = /^[0-9a-f]{64}$/u;
const FROZEN_PHYSICAL_CORE = {
  browserManifest: {
    byteCount: 14_711,
    sha256: "5095926cb961a1feafcc32edd41fff3e4690a0edf61598227cf463df893cf89d",
  },
  browserEntry: {
    byteCount: 1_168_270,
    sha256: "84d41d8c512f7c26c146357f87d235b0f8078bb95b02e7ed640e67ce754b1d2c",
  },
  verifierManifest: {
    byteCount: 5_680,
    sha256: "11ab173c02f007bedad69907a1c596719131061c747e1d286e79947b42a4f726",
  },
  verifierEntry: {
    byteCount: 330_813,
    sha256: "df0e0b99f731ea8e30abaf3eb2785c821b67ce994cbffc9d4c7ade8ecab5f469",
  },
} as const;
const REQUIRED_EXPORTS = [
  "EXACT_PINS",
  "OpfsPhysicalReleaseSink",
  "PhysicalAppAuditBridge",
  "WorkerPhysicalAcceptanceVerifier",
  "createResumablePhysicalPhase1",
  "launchExactPrimeMach1BrowserAppFromInstallation",
  "launchExactPrimeMach1OfflinePageFromInstallation",
  "parseResumablePhysicalPhase1",
  "resumeResumablePhysicalAcceptance",
] as const;

export interface ExactAssetRef {
  path: string;
  byteCount: number;
  sha256: string;
  role?: string;
}

export interface ExactRuntimeAssetsManifest {
  kind: "mach1-browser-runtime-assets";
  schemaVersion: 2;
  qualified: boolean;
  proofScope: string;
  backendSourceStampSha256: string;
  modelManifestSha256: string;
  tensorIndexSha256: string;
  runtime: {
    default: string;
    defaultJavascript: string;
    compat: { worker: string; wasm: string };
  };
  browserApp: {
    manifest: ExactAssetRef;
    entry: ExactAssetRef;
    publicExports: string[];
  };
  physicalVerifier: {
    manifest: ExactAssetRef;
    entry: ExactAssetRef;
  };
  physicalCore: {
    manifest: ExactAssetRef;
    entry: ExactAssetRef;
    frozen: true;
  };
  siteAutomation: {
    manifest: ExactAssetRef;
    entry: ExactAssetRef;
    frozen: true;
  };
  assets: ExactAssetRef[];
  limitations: string[];
}

export interface ExactPins {
  primeAgent: {
    version: "0.7.1";
    commit: string;
  };
  wllama: { commit: string };
  machRuntime: { commit: string };
  model: {
    revision: string;
    architecture: "mach1";
    formatVersion: 3;
    tensorCount: 1637;
    ggufSha256: string;
    tensorIndexSha256: string;
    chatTemplateSha256: string;
    shardManifestSha256: string;
  };
}

export interface ExactPrimeSessionView {
  sessionId: string;
  sessionName: string;
  parentSessionId: string | null;
  workerId: string;
  kernelId: string;
  agentInstanceId: string;
  agent: {
    state: {
      messages: unknown[];
      isStreaming: boolean;
      errorMessage?: string;
    };
  };
  prompt(input: string): Promise<void>;
}

export interface ExactPrimeAppView {
  readonly appInstanceId: string;
  readonly isDisposed: boolean;
  root: ExactPrimeSessionView;
  readonly activeSessions: readonly ExactPrimeSessionView[];
  readonly modelIdentity: { modelLoadId: string };
  prompt(input: string): Promise<void>;
  spawnChild(prompt: string, name?: string): Promise<{
    rlmChildId: string;
    sessionId: string;
    completion: Promise<void>;
  }>;
  subscribeAudit(listener: (event: unknown) => void): () => void;
  dispose(): Promise<void>;
}

export interface ExactRuntimeView {
  readonly runtimeInstanceId: string;
  readonly isDisposed: boolean;
  getEvidenceSnapshot(): unknown;
  getPagerLoaderReceipt(): { path: string; bytes: Uint8Array };
  dispose(): Promise<void>;
  exit(): Promise<void>;
}

export interface ExactLaunchedApp {
  runtime: ExactRuntimeView;
  app: ExactPrimeAppView;
  verification: unknown;
}

export function assertExactLaunchDisposed(launched: ExactLaunchedApp, label: string): void {
  if (launched.app.isDisposed !== true || launched.runtime.isDisposed !== true) {
    throw new Error(`${label} retained an active Prime app or exact MACH-1 runtime`);
  }
}

export async function disposeExactLaunch(launched: ExactLaunchedApp, label: string): Promise<void> {
  await launched.app.dispose();
  assertExactLaunchDisposed(launched, label);
}

export interface ExactOnlineCheckpoint {
  kind: "exact-prime-mach1-online-page-checkpoint";
  schemaVersion: 1;
  pageInstanceId: string;
  runtimeInstanceId: string;
  appInstanceId: string;
  modelLoadId: string;
  rootPersistence: {
    kind: "prime-browser-opfs-root-state";
    schemaVersion: 1;
    sessionId: string;
    messageCount: number;
    stateSha256: string;
    proofKind: "opfs";
  };
  shardManifestSha256: string;
  tensorIndexSha256: string;
  contextWindow: number;
  memory: unknown;
  createdAt: string;
}

export interface ExactOfflineAttestation {
  kind: "exact-prime-mach1-offline-page-attestation";
  schemaVersion: 1;
  priorPageInstanceId: string;
  pageInstanceId: string;
  priorRuntimeInstanceId: string;
  runtimeInstanceId: string;
  priorAppInstanceId: string;
  appInstanceId: string;
  priorModelLoadId: string;
  modelLoadId: string;
  crossOriginIsolated: true;
  navigatorOnline: false;
  serviceWorkerControlled: true;
  rootPersistence: ExactOnlineCheckpoint["rootPersistence"];
  shardManifestSha256: string;
  tensorIndexSha256: string;
  contextWindow: number;
  memory: unknown;
  createdAt: string;
}

export interface ExactOfflineLaunchedApp extends ExactLaunchedApp {
  phase2: ExactOfflineAttestation;
}

export interface ExactBrowserModule {
  EXACT_PINS: ExactPins;
  launchExactPrimeMach1BrowserAppFromInstallation(options: Record<string, unknown>): Promise<ExactLaunchedApp>;
  createExactPrimeMach1OnlinePageCheckpoint(launched: ExactLaunchedApp): Promise<ExactOnlineCheckpoint>;
  launchExactPrimeMach1OfflinePageFromInstallation(options: Record<string, unknown>): Promise<ExactOfflineLaunchedApp>;
  PhysicalAppAuditBridge: new () => {
    listener: (event: unknown) => void;
    handoff(app: ExactPrimeAppView): void;
    snapshot(): unknown[];
    close(): void;
  };
  WorkerPhysicalAcceptanceVerifier: {
    create(
      binding: { manifestUrl: URL | string; manifestByteCount: number; manifestSha256: string },
      timeoutMs?: number,
    ): Promise<{
      readonly proofKind: "independent-worker";
      dispose(): void;
    }>;
  };
  OpfsPhysicalReleaseSink: new (root: FileSystemDirectoryHandle) => {
    readonly proofKind: "opfs-release-marker";
  };
  createResumablePhysicalPhase1(options: Record<string, unknown>): Promise<ResumablePhysicalPhase1Envelope>;
  parseResumablePhysicalPhase1(
    envelope: Pick<ResumablePhysicalPhase1Envelope, "descriptorBytes" | "descriptorByteCount" | "descriptorSha256">,
  ): Promise<ResumablePhysicalPhase1Descriptor>;
  resumeResumablePhysicalAcceptance(options: Record<string, unknown>): Promise<ResumedPhysicalAcceptanceResult>;
}

export interface ResumablePhysicalPhase1Envelope {
  kind: "prime-mach1-resumable-physical-phase1-envelope";
  schemaVersion: 1;
  descriptorBytes: Uint8Array;
  descriptorByteCount: number;
  descriptorSha256: string;
}

export interface ResumablePhysicalPhase1Descriptor {
  kind: "prime-mach1-resumable-physical-phase1";
  schemaVersion: 1;
  runId: string;
  exactCheckpoint: ExactOnlineCheckpoint;
  scenario: {
    children: Array<{ rlmChildId: string }>;
    dynamicSkill: Record<string, unknown>;
    refinement: Record<string, unknown>;
  };
}

export interface ResumedPhysicalAcceptanceResult {
  phase2: {
    recoveredFileValue: string;
    recoveredResult: string;
    secondWorkerId: string;
    recoveryObservation: { text: string; byteCount: number; sha256: string };
  };
  navigation: {
    serviceWorkerAuditByteCount: number;
    serviceWorkerAuditSha256: string;
  };
  bundle: {
    runId: string;
    release: { verification: { verifierBuildSha256: string } };
    releaseBytes: Uint8Array;
    releaseSha256: string;
  };
}

export interface LoadedExactRuntimeBundle {
  manifest: ExactRuntimeAssetsManifest;
  module: ExactBrowserModule;
  primeWorker: ExactAssetRef;
  manifestSha256: string;
  serviceWorkerBuildId: string;
}

function exactAsset(value: unknown, allowedPrefixes: readonly string[]): value is ExactAssetRef {
  const asset = value as ExactAssetRef;
  return Boolean(
    asset &&
    typeof asset.path === "string" &&
    allowedPrefixes.some((prefix) => asset.path.startsWith(prefix)) &&
    !asset.path.includes("..") &&
    Number.isSafeInteger(asset.byteCount) &&
    asset.byteCount >= 0 &&
    SHA256.test(asset.sha256),
  );
}

function assertRuntimeManifest(value: unknown): asserts value is ExactRuntimeAssetsManifest {
  const manifest = value as ExactRuntimeAssetsManifest;
  if (
    manifest?.kind !== "mach1-browser-runtime-assets" ||
    manifest.schemaVersion !== 2 ||
    typeof manifest.qualified !== "boolean" ||
    typeof manifest.proofScope !== "string" ||
    manifest.backendSourceStampSha256 !== "53749b792ee5ff2f5b2c85c394f8c78d1d2bb44b34f258c969aea84f6e1f9378" ||
    manifest.modelManifestSha256 !== "9f068429675c3e92b0d6fa39cd177a487675487bfcc9532d746aaf6c1f1c5921" ||
    manifest.tensorIndexSha256 !== "469206f990e5345e8670b2613d60f0ec007d6f7b180280d2922c8dec114e2383" ||
    manifest.runtime?.default !== "/runtime/wllama.wasm" ||
    manifest.runtime?.defaultJavascript !== "/runtime/wllama.js" ||
    manifest.runtime?.compat?.worker !== "/runtime-compat/wllama.js" ||
    manifest.runtime?.compat?.wasm !== "/runtime-compat/wllama.wasm" ||
    !exactAsset(manifest.browserApp?.manifest, ["/runtime/exact-app/"]) ||
    !exactAsset(manifest.browserApp?.entry, ["/runtime/exact-app/"]) ||
    !REQUIRED_EXPORTS.every((name) => manifest.browserApp.publicExports?.includes(name)) ||
    !exactAsset(manifest.physicalVerifier?.manifest, ["/runtime/physical-verifier/"]) ||
    !exactAsset(manifest.physicalVerifier?.entry, ["/runtime/physical-verifier/"]) ||
    !exactAsset(manifest.physicalCore?.manifest, ["/runtime/exact-app/"]) ||
    !exactAsset(manifest.physicalCore?.entry, ["/runtime/exact-app/"]) ||
    manifest.physicalCore.frozen !== true ||
    !exactAsset(manifest.siteAutomation?.manifest, ["/runtime/site-automation/"]) ||
    !exactAsset(manifest.siteAutomation?.entry, ["/runtime/site-automation/"]) ||
    manifest.siteAutomation.frozen !== true ||
    !Array.isArray(manifest.assets) ||
    manifest.assets.length < 11 ||
    !manifest.assets.every((asset) => exactAsset(asset, ["/runtime/", "/runtime-compat/"])) ||
    new Set(manifest.assets.map((asset) => asset.path)).size !== manifest.assets.length ||
    manifest.qualified !== false ||
    Object.hasOwn(manifest, "releaseBinding") ||
    !Array.isArray(manifest.limitations)
  ) {
    throw new Error("Site runtime-assets manifest is not the exact schema-2 Prime MACH-1 bundle");
  }
  const byPath = new Map(manifest.assets.map((asset) => [asset.path, asset]));
  for (const [path, byteCount, sha256Value] of [
    ["/runtime/wllama.wasm", 9_111_164, "3aac255736893680486584ef2ecb11550aa5da11d27af866d7e34a17685f147d"],
    ["/runtime/wllama.js", 139_296, "247f1ef2418a0b6ecd06809a13438655e306b7e737a7c127c0ef6adab7fecbc4"],
    ["/runtime-compat/wllama.js", 170_541, "44fee64b816f35b28c8cc17c6d9edbf01eac61d0938d3dc178f3d98ad335a70d"],
    ["/runtime-compat/wllama.wasm", 16_915_922, "e0552906c2b2ab5dad2c9990eb0356866942580e1a4a30859d5ef61c9aeb8e54"],
    ["/runtime/receipts/base-build.json", 5_404, "1d5ad2e117aec59af0006ec154a5d105f8a08899b9e539548a3fa27dd6085bad"],
    ["/runtime/receipts/integration-build.json", 10_272, "7ca8ee7ac26e85077433daeb7c6e4d811cc4b5cf7c903a898ceb15feb5684b9b"],
    ["/runtime/receipts/native-cpu-oracle.json", 1_935, "26f9b70cb8aa92820fd1c9d637ea6f7f77439b6e78c2f9bb6f1d8451c9a1bf6b"],
    ["/runtime/receipts/standalone-conformance.json", 7_081, "3ff9e6e56cb67cba2174676e53cd8cce1893d8d99f2059311e98029be41be570"],
  ] as const) {
    const asset = byPath.get(path);
    if (asset?.byteCount !== byteCount || asset.sha256 !== sha256Value) {
      throw new Error(`Runtime manifest drifted from the frozen artifact ${path}`);
    }
  }
  for (const bound of [
    manifest.browserApp.manifest,
    manifest.browserApp.entry,
    manifest.physicalVerifier.manifest,
    manifest.physicalVerifier.entry,
    manifest.physicalCore.manifest,
    manifest.physicalCore.entry,
    manifest.siteAutomation.manifest,
    manifest.siteAutomation.entry,
  ]) {
    const asset = byPath.get(bound.path);
    if (!asset || asset.byteCount !== bound.byteCount || asset.sha256 !== bound.sha256) {
      throw new Error(`Runtime manifest nested binding drifted for ${bound.path}`);
    }
  }
  if (
    manifest.browserApp.manifest.byteCount !== FROZEN_PHYSICAL_CORE.browserManifest.byteCount ||
    manifest.browserApp.manifest.sha256 !== FROZEN_PHYSICAL_CORE.browserManifest.sha256 ||
    manifest.browserApp.entry.byteCount !== FROZEN_PHYSICAL_CORE.browserEntry.byteCount ||
    manifest.browserApp.entry.sha256 !== FROZEN_PHYSICAL_CORE.browserEntry.sha256 ||
    manifest.physicalVerifier.manifest.byteCount !== FROZEN_PHYSICAL_CORE.verifierManifest.byteCount ||
    manifest.physicalVerifier.manifest.sha256 !== FROZEN_PHYSICAL_CORE.verifierManifest.sha256 ||
    manifest.physicalVerifier.entry.byteCount !== FROZEN_PHYSICAL_CORE.verifierEntry.byteCount ||
    manifest.physicalVerifier.entry.sha256 !== FROZEN_PHYSICAL_CORE.verifierEntry.sha256 ||
    manifest.physicalCore.entry.sha256 !== manifest.browserApp.entry.sha256 ||
    manifest.physicalCore.manifest.sha256 !== manifest.browserApp.manifest.sha256
  ) {
    throw new Error("Resumable physical core is not the frozen browser-app build");
  }
}

async function fetchExactBytes(ref: ExactAssetRef, signal?: AbortSignal): Promise<Uint8Array> {
  const response = await fetch(ref.path, {
    cache: "no-store",
    credentials: "same-origin",
    signal,
  });
  if (!response.ok) throw new Error(`Exact runtime asset ${ref.path} failed (${response.status})`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength !== ref.byteCount || bytesToHex(sha256(bytes)) !== ref.sha256) {
    throw new Error(`Exact runtime asset identity failed: ${ref.path}`);
  }
  return bytes;
}

async function activeServiceWorkerBuildId(signal?: AbortSignal): Promise<string> {
  if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) {
    throw new Error("Exact runtime launch requires an active service worker controller");
  }
  const response = await fetch("/runtime/app-assets.json", {
    cache: "no-store",
    credentials: "same-origin",
    signal,
  });
  if (!response.ok) throw new Error(`Offline app identity failed (${response.status})`);
  const app = await response.json() as {
    kind?: string;
    schemaVersion?: number;
    cacheBuildId?: string;
    serviceWorker?: { buildId?: string };
  };
  if (
    app.kind !== "prime-mach1-offline-app-assets" ||
    app.schemaVersion !== 2 ||
    !SHA256.test(app.cacheBuildId ?? "") ||
    app.serviceWorker?.buildId !== app.cacheBuildId
  ) {
    throw new Error("Active service worker has no hash-bound app build identity");
  }
  return app.cacheBuildId!;
}

function assertPins(pins: ExactPins, installation: ExactMach1InstallationFiles): void {
  if (
    pins?.primeAgent?.version !== "0.7.1" ||
    !/^[0-9a-f]{40}$/u.test(pins.primeAgent.commit) ||
    !/^[0-9a-f]{40}$/u.test(pins.wllama?.commit ?? "") ||
    pins.machRuntime?.commit !== installation.manifest.splitter.revision ||
    pins.model?.revision !== installation.manifest.source.revision ||
    pins.model?.architecture !== "mach1" ||
    pins.model?.formatVersion !== 3 ||
    pins.model?.tensorCount !== 1_637 ||
    pins.model?.ggufSha256 !== installation.manifest.unsplit.sha256 ||
    pins.model?.tensorIndexSha256 !== installation.manifest.tensorIndex.sha256 ||
    pins.model?.chatTemplateSha256 !== installation.manifest.chatTemplate.sha256 ||
    pins.model?.shardManifestSha256 !== installation.manifestSha256
  ) {
    throw new Error("Loaded browser module does not expose the pinned Prime v0.7.1 + full MACH-1 identity");
  }
}

export async function loadExactRuntimeBundle(
  installation: ExactMach1InstallationFiles,
  signal?: AbortSignal,
): Promise<LoadedExactRuntimeBundle> {
  if (globalThis.crossOriginIsolated !== true) {
    throw new Error("Exact MACH-1 requires a cross-origin-isolated document");
  }
  if (installation.files.length !== 18) {
    throw new Error("Exact runtime launch requires all 18 verified MACH-1 OPFS Files");
  }
  const serviceWorkerBuildId = await activeServiceWorkerBuildId(signal);
  const response = await fetch("/runtime/runtime-assets.json", {
    cache: "no-store",
    credentials: "same-origin",
    signal,
  });
  if (!response.ok) throw new Error(`Runtime manifest unavailable (${response.status})`);
  const manifestBytes = new Uint8Array(await response.arrayBuffer());
  const manifest = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(manifestBytes)) as unknown;
  assertRuntimeManifest(manifest);
  const primeWorkers = manifest.assets.filter((asset) =>
    /^\/runtime\/exact-app\/assets\/entry-[A-Za-z0-9_-]+\.js$/u.test(asset.path),
  );
  if (primeWorkers.length !== 1) {
    throw new Error("Frozen exact browser module has no unique manifest-bound Prime Worker");
  }
  const primeWorker = primeWorkers[0]!;
  const primeWorkerModules = manifest.assets.filter((asset) =>
    asset.path.startsWith("/runtime/exact-app/assets/") && asset.path.endsWith(".js"),
  );
  if (primeWorkerModules.length < 2) {
    throw new Error("Frozen exact Prime Worker dependency set is incomplete");
  }
  await Promise.all([
    fetchExactBytes(manifest.browserApp.manifest, signal),
    fetchExactBytes(manifest.browserApp.entry, signal),
    fetchExactBytes(manifest.physicalVerifier.manifest, signal),
    fetchExactBytes(manifest.physicalVerifier.entry, signal),
    fetchExactBytes(manifest.siteAutomation.manifest, signal),
    fetchExactBytes(manifest.siteAutomation.entry, signal),
    // Preflight the exact module Worker before the 7.33 GiB runtime-owned
    // integrity pass. The frozen bundle's default absolute /assets URL is not
    // the staged path; exactLaunchOptions supplies this verified asset through
    // the bundle's supported workerFactory seam instead.
    ...primeWorkerModules.map((asset) => fetchExactBytes(asset, signal)),
  ]);
  const imported = await import(/* webpackIgnore: true */ /* @vite-ignore */ manifest.browserApp.entry.path) as unknown;
  const browserModule = imported as ExactBrowserModule;
  for (const name of REQUIRED_EXPORTS) {
    if (!(name in (browserModule as unknown as Record<string, unknown>))) {
      throw new Error(`Frozen exact browser module is missing ${name}`);
    }
  }
  assertPins(browserModule.EXACT_PINS, installation);
  return {
    manifest,
    module: browserModule,
    primeWorker,
    manifestSha256: bytesToHex(sha256(manifestBytes)),
    serviceWorkerBuildId,
  };
}

export function exactLaunchOptions(
  bundle: LoadedExactRuntimeBundle,
  installation: ExactMach1InstallationFiles,
  modelLoadId: string,
  onAuditEvent?: (event: unknown) => void,
  physicalNonce?: string,
  onWorkerError?: (error: Error) => void,
): Record<string, unknown> {
  const pins = bundle.module.EXACT_PINS;
  const assetUrl = (value: string): string => physicalNonce
    ? `${value}?physical_nonce=${encodeURIComponent(physicalNonce)}`
    : value;
  return {
    assets: {
      memory64WasmUrl: assetUrl(bundle.manifest.runtime.default),
      memory32WorkerUrlOrCode: assetUrl(bundle.manifest.runtime.compat.worker),
      memory32WasmUrl: assetUrl(bundle.manifest.runtime.compat.wasm),
    },
    modelFiles: installation.files,
    identity: {
      adapterKind: "exact-mach1-wllama",
      wllamaCommit: pins.wllama.commit,
      machRuntimeCommit: pins.machRuntime.commit,
      modelRevision: pins.model.revision,
      modelSha256: pins.model.ggufSha256,
      tensorIndexSha256: pins.model.tensorIndexSha256,
      templateSha256: pins.model.chatTemplateSha256,
      architecture: "mach1",
      formatVersion: 3,
      tensorCount: 1_637,
      fullModel: true,
      modelLoadId,
    },
    rootSessionId: "prime-root",
    rootSessionName: "root",
    workerFactory: (session: { workerId: string }) => {
      const worker = new Worker(
        assetUrl(bundle.primeWorker.path),
        { type: "module", name: session.workerId },
      );
      if (onWorkerError) {
        worker.addEventListener("error", (event) => {
          onWorkerError(new Error(`Prime Agent Worker failed to start: ${event.message || "module load failed"}`));
        }, { once: true });
      }
      return worker;
    },
    pyodideIndexUrl: "/runtime/pyodide/",
    pyodidePackageBaseUrl: "/runtime/pyodide/",
    generation: {
      maxTokens: 2_048,
      temperature: 0,
      topP: 1,
    },
    ...(onAuditEvent ? { onAuditEvent } : {}),
  };
}
